"use client";

import posthog from "posthog-js";
import { useEffect, useRef } from "react";

/**
 * PostHog vía paquete `posthog-js`: autocapture, pageviews y session replay.
 *
 * La ingesta se rutea por un reverse proxy same-origin (`/ingest`, ver
 * `next.config.ts` → `rewrites()`) para que los ad-blockers no la bloqueen
 * (no pega directo a `*.posthog.com`). No inicializa nada si no hay key —
 * la landing funciona igual sin analytics configurado.
 *
 * Env: NEXT_PUBLIC_POSTHOG_KEY (project API key, pública). El host ya NO se
 * configura por env: ingesta = `/ingest` (proxy), ui_host = us.posthog.com.
 */

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;

// Guard idempotente: el bootstrap corre una sola vez por carga de página
// (StrictMode en dev monta los efectos dos veces).
let initialized = false;

export function track(event: string, props?: Record<string, unknown>): void {
  if (POSTHOG_KEY && typeof window !== "undefined") {
    posthog.capture(event, props);
  }
}

/**
 * Bootstrap de PostHog. Montado una sola vez en la landing. No renderiza nada.
 */
export function PostHogSnippet() {
  useEffect(() => {
    if (!POSTHOG_KEY || initialized) return;
    initialized = true;
    posthog.init(POSTHOG_KEY, {
      api_host: "/ingest",
      ui_host: "https://us.posthog.com",
      defaults: "2025-05-24",
      person_profiles: "identified_only",
    });
  }, []);
  return null;
}

/**
 * Marca `section_viewed` una sola vez por sección cuando el 40% entra
 * en viewport. Hijo de cada <section> de la landing.
 */
export function SectionViewTracker({ section }: { section: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const seen = useRef(false);

  useEffect(() => {
    const el = ref.current?.parentElement;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!seen.current && entries.some((e) => e.isIntersecting)) {
          seen.current = true;
          track("section_viewed", { section });
          observer.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [section]);

  return <span ref={ref} aria-hidden className="hidden" />;
}
